import { type ReactNode, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { useAuthStore } from '../stores/useAuthStore';

export function AppRouteFallback() {
  return (
    <div className="flex min-h-screen items-center justify-center" role="status" aria-live="polite">
      <div
        className="h-8 w-8 animate-spin rounded-full border-4 border-line border-t-bg-inverse"
        aria-hidden="true"
      />
      <span className="sr-only">Loading page</span>
    </div>
  );
}

export function SessionLoading({ message }: { message: string }) {
  return (
    <div
      className="flex min-h-[70vh] items-center justify-center px-6"
      role="status"
      aria-live="polite"
    >
      <div className="w-full max-w-md rounded-2xl border border-line bg-paper p-6">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-full bg-ink text-lg font-semibold text-paper">
            WI
          </div>
          <div>
            <p className="text-sm uppercase tracking-[0.18em] text-muted">WhatImean</p>
            <h2 className="text-lg font-medium text-ink">{message}</h2>
          </div>
        </div>
        <div className="mt-5 flex items-center gap-2 text-ink/70">
          <div className="h-2 w-2 animate-bounce rounded-full bg-ink" aria-hidden="true" />
          <div
            className="h-2 w-2 animate-bounce rounded-full bg-ink-body [animation-delay:120ms]"
            aria-hidden="true"
          />
          <div
            className="h-2 w-2 animate-bounce rounded-full bg-ink-secondary [animation-delay:240ms]"
            aria-hidden="true"
          />
          <span className="text-sm">Preparing your experience</span>
        </div>
      </div>
    </div>
  );
}

export function SessionToast() {
  const sessionError = useAuthStore((state) => state.sessionError);
  const clearSessionError = useAuthStore((state) => state.clearSessionError);

  useEffect(() => {
    if (!sessionError) return;
    const timer = setTimeout(() => clearSessionError(), 4000);
    return () => clearTimeout(timer);
  }, [sessionError, clearSessionError]);

  if (!sessionError) return null;

  return (
    <div className="fixed right-4 top-4 z-50" role="alert">
      <div className="rounded-2xl border border-line bg-[var(--state-warning-bg)] px-4 py-3 text-sm text-state-warning shadow-overlay">
        {sessionError}
      </div>
    </div>
  );
}

function useSessionGate() {
  const user = useAuthStore((state) => state.user);
  const sessionStatus = useAuthStore((state) => state.sessionStatus);
  const isCheckingSession = useAuthStore((state) => state.isCheckingSession);
  const checkSession = useAuthStore((state) => state.checkSession);

  useEffect(() => {
    if (sessionStatus === 'unknown') {
      void checkSession();
    }
  }, [sessionStatus, checkSession]);

  return {
    user,
    sessionStatus,
    isCheckingSession,
  };
}

export function ProtectedRoute({ children }: { children: ReactNode }) {
  const location = useLocation();
  const { user, sessionStatus, isCheckingSession } = useSessionGate();

  if (sessionStatus === 'unknown' || isCheckingSession) {
    return <SessionLoading message="Checking your session..." />;
  }

  if (!user || sessionStatus !== 'authenticated') {
    return (
      <Navigate
        to="/login"
        replace
        state={{ returnTo: `${location.pathname}${location.search}${location.hash}` }}
      />
    );
  }

  if (user.role === 'admin') {
    const isIntentionalLearnerPreview =
      new URLSearchParams(location.search).get('adminPreview') === '1';
    if (!isIntentionalLearnerPreview) {
      return <Navigate to="/admin" replace />;
    }
  }

  return children;
}

export function AdminRoute({ children }: { children: ReactNode }) {
  const location = useLocation();
  const { user, sessionStatus, isCheckingSession } = useSessionGate();

  if (sessionStatus === 'unknown' || isCheckingSession) {
    return <SessionLoading message="Checking admin access..." />;
  }

  if (!user || sessionStatus !== 'authenticated') {
    return (
      <Navigate
        to="/login"
        replace
        state={{ returnTo: `${location.pathname}${location.search}${location.hash}` }}
      />
    );
  }

  if (user.role !== 'admin') {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}
